<template>
  <div :class="bem()">
    <div
      v-if="!hasChildRoute"
      :class="bem('header')"
    >
      <VisionHeading
        tag="h2"
        size="h1"
      >
        Questions?
      </VisionHeading>

      <VisionHeading
        :class="bem('subheading')"
        tag="h3"
        size="h3"
      >
        We're here to help.
      </VisionHeading>
    </div>

    <SupportSearch
      v-if="articles.length && !hasChildRoute"
      :articles="articles"
    />

    <SupportCategories
      v-if="!hasChildRoute"
      :categories="categories"
      :articles="articles"
    />

    <section
      :class="bem('dashboard-content')"
    >
      <NuxtPage
        v-if="hasChildRoute"
        :categories="categories"
        :articles="articles"
      />
    </section>

    <div :class="bem('contact')">
      <VisionHeading
        tag="h4"
        text="Didn't find an answer to your question?"
      />

      <div :class="bem('actions', { whatsappSupportEnabled })">
        <VisionButton
          button-text="Contact Us"
          background-colour="gradient"
          :class="bem('action')"
          @click="handleContactUsClick"
        >
          <template #content>
            <span :class="bem('action-content')">
              <VisionIcon
                icon="envelope"
                size="small"
                :class="bem('action-icon')"
              />
              Contact Us
            </span>
          </template>
        </VisionButton>

        <VisionButton
          v-if="whatsappSupportEnabled"
          to="https://wa.me/447360280003"
          background-colour="whatsapp"
          :class="bem('action')"
        >
          <template #content>
            <span :class="bem('action-content')">
              <VisionIcon
                icon="whatsapp"
                size="small"
                :class="bem('action-icon')"
              />
              Chat on WhatsApp
            </span>
          </template>
        </VisionButton>
      </div>
    </div>
  </div>
</template>

<script setup>
const { runWithContext } = useNuxtApp()
const userStore = useUserStore()
const { getTitle, getDescription } = useMetaHelper()
const globalConfigStore = useGlobalConfigStore()
const route = useRoute()
const bem = useBEM('SupportPage')

runWithContext(() =>
  useHead({
    title: getTitle('Support'),
    meta: [
      ...getDescription("Questions? We're here to help!"),
      {
        hid: 'og:title',
        property: 'og:title',
        content: getTitle('Support'),
      },
    ],
  })
)

definePageMeta({
  name: 'SupportPage',
  pageMeta: 'Support Page'
})

const whatsappSupportEnabled = globalConfigStore.isFlagEnabled('whatsapp-support-enabled')

const { data: supportData } = await useFetch('/support-articles')

// Extract articles and categories from the fetched data
const articles = supportData.value.articles
const rawCategories = supportData.value.collections

// Function to recursively nest categories
const nestCategories = (categories, parentId = null, level = 0) => {
  return categories
    .filter(category => category.parent_id === parentId)
    .map(category => {
      // Nested categories under the current category
      const nestedCategories = nestCategories(
        categories,
        category.id,
        level + 1
      )

      // Articles directly under the current category
      const categoryArticles = articles.filter(article =>
        article.parent_ids?.includes(parseInt(category.id))
      )

      return {
        ...category,
        level,
        childArticleCount:
          categoryArticles.length + nestedCategories.reduce(
            (acc, curr) => acc + curr.childArticleCount,
            0
          ),
        articles: categoryArticles,
        // Include nested categories if they exist
        categories: nestedCategories.length ? nestedCategories : undefined,
      }
    })
    .sort((a, b) => a.order - b.order)
}

// Initiate nesting from the root categories (those without a parent_id)
const categories = nestCategories(rawCategories).filter(
  category => category.articles.length || category.categories?.length
)

const hasChildRoute = computed(() => {
  return route.matched.length > 1
})

onMounted(() => {
  /* eslint-disable */
  (function () { var w = window; var ic = w.Intercom; if (typeof ic === "function") { ic('reattach_activator'); ic('update', w.intercomSettings) } else { var d = document; var i = function () { i.c(arguments) }; i.q = []; i.c = function (args) { i.q.push(args) }; w.Intercom = i; var l = function () { var s = d.createElement('script'); s.type = 'text/javascript'; s.async = true; s.src = 'https://widget.intercom.io/widget/n86kmuim'; var x = d.getElementsByTagName('script')[0]; x.parentNode.insertBefore(s, x) }; if (document.readyState === 'complete') { l() } else if (w.attachEvent) { w.attachEvent('onload', l) } else { w.addEventListener('load', l, false) } } })()
  /* eslint-enable */

  const intercomSettings = {
    api_base: "https://api-iam.intercom.io",
    app_id: "n86kmuim"
  }

  if (userStore.email) {
    intercomSettings.email = userStore.email
  }

  window.Intercom("boot", intercomSettings)
})

const handleContactUsClick = () => {
  window.Intercom('showNewMessage')
}
</script>

<style lang="postcss">
.SupportPage {
  @apply global-wrapper
    flex flex-col gap-8 pt-8;

  @screen sm {
    --screen-size: 1200px;
  }
}

.SupportPage__contact {
  @apply text-center;
}

.SupportPage__actions {
  @apply flex
    max-w-xs
    flex-col md:flex-row
    md:justify-center
    gap-3 md:gap-x-4
    mt-sm ml-auto mr-auto;
}

.SupportPage__actions--whatsapp-enabled {
  @apply max-w-xs md:max-w-[600px];
}

.SupportPage__action {
  @apply md:flex-1;
}

.SupportPage__action-icon {
  @apply mr-2;
}

.SupportPage__action-content {
  @apply flex items-center justify-center whitespace-nowrap;
}

.SupportPage__subheading {
  @apply text-grey-600;
}
</style>
